import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { editCurrentUser } from "Auth/state";
import { AUTH_ACTIONS } from "Auth/types";
import { useStateActionMeta } from "Auth/utils";
import { EditUserProfile as EditUserProfileComponent } from "Auth/components";
import { ChangePassword, DeleteAccount, ChangeEmailRequest } from "Auth/containers";

import { useProfileContext } from "Auth/contexts";
import { IAPIState, ISex } from "shared/types";
import { dateStringToISO } from "shared/utils";

interface EditUserProfileProps {
  showOtherInfo?: boolean;
  redirectAfterEdit?: () => void;
}

export interface IEditUserProfile {
  personalInfo: {
    firstname?: string;
    lastname?: string;
  };
  loginInfo: {
    email?: string;
  };
  otherInfo: {
    sex?: ISex;
    birthDate?: string;
    birthCountry?: string;
    nationality?: string;
  };
}

export const EditUserProfile: React.FC<EditUserProfileProps> = ({
  showOtherInfo,
  redirectAfterEdit,
}) => {
  const { currentUser, email, handleLogout } = useProfileContext();

  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [showDeleteUser, setShowDeleteUser] = useState(false);

  const { status, error, message } = useStateActionMeta(AUTH_ACTIONS.editCurrentUser) || {};
  const dispatch = useDispatch();

  useEffect(() => {
    if (redirectAfterEdit && status === IAPIState.succeeded) {
      redirectAfterEdit();
    }
  }, [status, redirectAfterEdit]);

  const onSubmit = (data: IEditUserProfile) => {
    const {
      personalInfo: { firstname, lastname },
      otherInfo,
    } = data;

    const formattedDate = otherInfo?.birthDate ? dateStringToISO(otherInfo.birthDate) : null;

    dispatch(
      editCurrentUser({
        firstname,
        lastname,
        ...otherInfo,
        ...(otherInfo
          ? {
              birthDate: formattedDate,
            }
          : {}),
      })
    );
  };

  return (
    <>
      <EditUserProfileComponent
        currentUser={currentUser}
        onSubmit={onSubmit}
        handleLogout={handleLogout}
        status={status}
        error={error ? message?.body : undefined}
        hasError={!!error}
        showOtherInfo={showOtherInfo}
        showChangeEmail={() => setShowChangeEmail(true)}
        showChangePassword={() => setShowChangePassword(true)}
        showDeleteUser={() => setShowDeleteUser(true)}
        apiError={error}
      />
      {showChangePassword && <ChangePassword handleClose={() => setShowChangePassword(false)} />}
      {showChangeEmail && email && (
        <ChangeEmailRequest oldEmail={email} handleClose={() => setShowChangeEmail(false)} />
      )}
      {showDeleteUser && <DeleteAccount handleClose={() => setShowDeleteUser(false)} />}
    </>
  );
};
